import type {
  EmailPinCodeAccessError,
  EmailPinSendResult,
  EmailPinSignInResult,
} from '@src/models/interfaces/emailPin.interface';
import EmailPinRepository from '@src/repositories/EmailPinRepository';
import safeExecute from '@src/util/safeExecute';

const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

class EmailPinService {
  private readonly repository: EmailPinRepository;

  constructor(repository: EmailPinRepository = new EmailPinRepository()) {
    this.repository = repository;
  }

  sendTemporaryPin(email: string): Promise<EmailPinSendResult> {
    return safeExecute(
      async () => {
        const normalizedEmail = email?.trim().toLowerCase();

        if (!normalizedEmail || !EMAIL_PATTERN.test(normalizedEmail)) {
          return { success: false, error: 'invalid_email' };
        }

        return this.repository.sendPin({ email: normalizedEmail });
      },
      'EmailPinService.sendTemporaryPin',
      { email },
    );
  }

  signInWithPin(email: string, code: string): Promise<EmailPinSignInResult> {
    return safeExecute(
      async () => {
        const normalizedEmail = email?.trim().toLowerCase();
        const normalizedCode = code?.trim().toUpperCase();

        if (!normalizedEmail || !normalizedCode) {
          return { success: false, error: 'invalid_code' };
        }

        const result = await this.repository.codeAccess({
          email: normalizedEmail,
          code: normalizedCode,
        });

        if (!result?.success || !result.token) {
          return { success: false, error: (result?.error ?? 'invalid_code') as EmailPinCodeAccessError };
        }

        const uid = await this.repository.signInWithCustomToken(result.token);

        if (!uid) {
          return { success: false, error: 'invalid_code' };
        }

        return { success: true, uid };
      },
      'EmailPinService.signInWithPin',
      { email },
    );
  }
}

export default EmailPinService;
